import type React from "react";
import { useCallback, useEffect, useState } from "react";
import useApiHook from "../../../utils/useApi";
import DataTable, { type TableColumn } from "react-data-table-component";
import Loading from "../../../merchant_dashboard/components/loading/Loading";
import { customStyles_T } from "../../../utils/table_utils";
import type {
  ApiInterswitchTransactionResponse,
  InterswitchTransactionData,
} from "../../../interface/interfaces";
import TransactionModal from "./TransactionModal";
import SearchComponent from "./SearchComponent";

const InterswitchTransactions = () => {
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(20);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [statusQuery, setStatusQuery] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");
  const [debouncedStatus, setDebouncedStatus] = useState<string>("");

  const [selectedTransaction, setSelectedTransaction] =
    useState<InterswitchTransactionData | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const { data, isLoading, makeRequest } =
    useApiHook<ApiInterswitchTransactionResponse>("/thirdparties/interswitch", {
      method: "get",
    });

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedStatus(statusQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [statusQuery]);

  const fetchTransactions = useCallback(async () => {
    const params = {
      page,
      size: perPage,
      search: debouncedSearch.trim() || "",
      status: debouncedStatus.trim() || "",
    };

    const response = await makeRequest(params);

    if (response?.data?.statusCode !== "00") {
      setTotalRows(0);
    } else if (response?.data?.data) {
      setTotalRows(response.data.data.totalCount || 0);
    }
  }, [page, perPage, debouncedSearch, debouncedStatus]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const exportToCSV = () => {
    if (!data?.data?.data || data.data.data.length === 0) {
      alert("No data to export");
      return;
    }

    const csvHeaders = [
      "Payment ID",
      "Reference",
      "Interswitch Payment ID",
      "Response Code",
      "Response Message",
      "HTTP Response Code",
      "Visa ECI Flag",
      "Visa Transaction ID",
      "Visa Term URL",
      "Date Created",
      "Date Updated",
      "Date Completed",
    ];

    const csvData = data.data.data.map(
      (transaction: InterswitchTransactionData) => [
        transaction.paymentId,
        transaction.reference,
        transaction.interswitchPaymentId,
        transaction.interswitchResponseCode,
        transaction.interswitchResponseMessage,
        transaction.httpResponseCode,
        transaction.visaEciFlag || "",
        transaction.visaTransactionId || "",
        transaction.visaTermUrl || "",
        new Date(transaction.dateCreated).toLocaleString(),
        new Date(transaction.dateUpdated).toLocaleString(),
        transaction.dateCompleted
          ? new Date(transaction.dateCompleted).toLocaleString()
          : "",
      ]
    );

    const csvContent = [csvHeaders, ...csvData]
      .map((row) => row.map((field) => `"${field}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `interswitch_transactions_${new Date().toISOString().split("T")[0]}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleViewMore = (transaction: InterswitchTransactionData) => {
    setSelectedTransaction(transaction);
    setIsModalOpen(true);
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setPage(1);
  };

  const handleStatusChange = (value: string) => {
    setStatusQuery(value);
    setPage(1);
  };

  const handleClearFilters = () => {
    setSearchQuery("");
    setStatusQuery("");
    setPage(1);
  };

  const columns: TableColumn<InterswitchTransactionData>[] = [
    {
      name: "Reference",
      selector: (row) => row.reference,
      sortable: true,
      cell: (row) => (
        <div className="font-medium text-gray-900">{row.reference}</div>
      ),
    },
    {
      name: "Payment ID",
      selector: (row) => row.paymentId,
      sortable: true,
      cell: (row) => <div className="text-gray-900">{row.paymentId}</div>,
    },
    {
      name: "Response Code",
      selector: (row) => row.interswitchResponseCode,
      sortable: true,
      cell: (row) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium ${
            row.interswitchResponseCode === "00"
              ? "bg-green-100 text-green-800"
              : "bg-red-100 text-red-800"
          }`}
        >
          {row.interswitchResponseCode}
        </span>
      ),
    },
    {
      name: "HTTP Code",
      selector: (row) => row.httpResponseCode,
      sortable: true,
      cell: (row) => (
        <div className="text-gray-900">{row.httpResponseCode}</div>
      ),
    },
    {
      name: "Date Created",
      selector: (row) => row.dateCreated,
      sortable: true,
      cell: (row) => (
        <div className="text-gray-900 text-sm">
          {new Date(row.dateCreated).toLocaleDateString()}
        </div>
      ),
    },
    {
      name: "Actions",
      cell: (row) => (
        <button
          onClick={() => handleViewMore(row)}
          className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 transition-colors"
        >
          View More
        </button>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];

  // Handle page change
  const handlePageChange = (page: number) => {
    setPage(page);
  };

  // Handle rows per page change
  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setPerPage(newPerPage);
    setPage(page);
  };

  return (
    <section>
      <div className="mb-4 flex justify-between items-center">
        <div className="flex-1">
          <SearchComponent
            searchQuery={searchQuery}
            statusQuery={statusQuery}
            onSearchChange={handleSearchChange}
            onStatusChange={handleStatusChange}
            onClearFilters={handleClearFilters}
          />
        </div>
        <button
          onClick={exportToCSV}
          disabled={!data?.data?.data || data.data.data.length === 0}
          className="ml-4 px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
        >
          Export CSV
        </button>
      </div>

      <DataTable
        columns={columns}
        data={data?.data?.data || []}
        progressPending={isLoading}
        progressComponent={<Loading />}
        pagination
        paginationServer
        paginationTotalRows={totalRows}
        onChangePage={handlePageChange}
        onChangeRowsPerPage={handlePerRowsChange}
        paginationPerPage={perPage}
        paginationRowsPerPageOptions={[10, 20, 30, 50]}
        highlightOnHover
        responsive
        persistTableHead
        customStyles={customStyles_T}
        noDataComponent={
          <div className="p-6 text-center text-gray-500">
            No transactions found
          </div>
        }
      />

      <TransactionModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        transaction={selectedTransaction}
      />
    </section>
  );
};

export default InterswitchTransactions;
